/**
 * Chemistry upload: Excel row → API payload helpers and worksheet readers.
 * Routing and the upload registry live in `chemUploadResolve.js`, `ChemUploadRegistry.js`, and `chemUploadFlows.js`.
 */
/* eslint-disable no-restricted-syntax, no-continue */
import * as XLSX from "xlsx";
import JSPDF from "jspdf";
import "jspdf-autotable";
import { normalizeFileName } from "./ChemUploadResolve";
import { checkLessThan } from "./Helper";

const GENISTA_ID_ROW_PATTERN = /GB/;

const PERCENT_UNIT_OPTIONS = ["%", "percent", "percentage"];

/**
 * Legacy `readXcelFile` sheet index (`ChemUpload-deprecated.js` `onSubmitProduct`).
 * Water / Brix templates store data on non-first sheets.
 *
 * @param {string} fileName
 * @returns {number}
 */
export function getLegacyChemUploadPrimarySheetIndexFromFileName(fileName) {
  const nameLower = normalizeFileName(fileName);
  if (nameLower.includes("water")) {
    return 2;
  }
  if (nameLower.includes("brix")) {
    return 4;
  }
  return 0;
}

/**
 * Aflatoxin row shape from legacy template (B1/B2/G1/G2 + combined).
 * @param {Array} row
 * @param {number} idCell
 * @param {number} resultCell
 * @returns {object}
 */
export function buildAflatoxinRowPayload(row, idCell, resultCell) {
  let errorCellName = null;
  const Genista_id = row[idCell].trim();
  let Result = "";

  const keyCells = [
    ["B1", 2],
    ["B2", 3],
    ["G1", 4],
    ["G2", 5],
  ];
  const detail_result = {};

  keyCells.every(([key, cellIndex]) => {
    if (row[cellIndex] === "n.a.") {
      detail_result[key] = "0.000";
    } else if (typeof row[cellIndex] === "number") {
      detail_result[key] = row[cellIndex].toFixed(3);
    } else {
      errorCellName = key;
      return false;
    }
    return true;
  });

  if (errorCellName) {
    return { error: { errorCellName, Genista_id } };
  }

  if (row[resultCell] === "n.a.") {
    Result = typeof row[7] === "number" ? row[7].toFixed(3) : "";
    errorCellName = "M1";
  } else if (typeof row[resultCell] === "number") {
    Result = row[resultCell].toFixed(3);
  } else {
    errorCellName = "B1+B2+G1+G2";
  }

  const retValue = {
    Genista_id,
    Result,
    detail_result,
  };
  return errorCellName ? { error: { errorCellName, Genista_id } } : retValue;
}

/**
 * Coerces XLSX cell values (number or numeric string) to a fixed-decimal string; otherwise "" or raw string.
 * @param {unknown} cellValue
 * @param {number} fractionDigits
 * @returns {string}
 */
function formatLegacyDynamicNumericCell(cellValue, fractionDigits) {
  if (cellValue === undefined || cellValue === null || cellValue === "") {
    return "";
  }
  if (typeof cellValue === "number" && !Number.isNaN(cellValue)) {
    return cellValue.toFixed(fractionDigits);
  }
  const trimmed = String(cellValue).trim();
  if (trimmed === "" || trimmed === "default") {
    return "";
  }
  const n = parseFloat(trimmed.replace(/,/g, ""));
  if (!Number.isNaN(n)) {
    return n.toFixed(fractionDigits);
  }
  return trimmed;
}

/**
 * Legacy `parseXcel` body: one result per GB row, rules driven by file name substring
 * (same as `ChemUpload-deprecated.js` `parseXcel` e.g. water workbook via `"water"` in file name, not selected test label).
 * @param {object} params
 * @param {Array<Array>} params.rows
 * @param {string} params.fileName
 * @param {number} params.idCell
 * @param {number} params.resultCell
 * @returns {{ list: Array, aflatoxinError: object|null }}
 */
export function buildLegacyDynamicExcelResultList({
  rows, fileName, idCell, resultCell,
}) {
  const list = [];
  const nameLower = normalizeFileName(fileName);
  let aflatoxinError = null;

  for (const row of rows) {
    if (!row[idCell] || String(row[idCell]).indexOf("GB") <= -1) {
      continue;
    }

    const genistaId = String(row[idCell]).trim();

    if (nameLower.indexOf("peroxide") > -1 || nameLower.indexOf("patulin") > -1) {
      let resultValue = "default";
      const indexSmall = row[resultCell].toString().indexOf("<");

      if (typeof row[resultCell] === "number") {
        resultValue = row[resultCell].toFixed(3);
      } else if (
        (indexSmall === undefined || indexSmall < 0)
        && typeof row[resultCell] === "string"
      ) {
        if (!Number.isNaN(parseFloat(row[resultCell]))) {
          resultValue = parseFloat(row[resultCell]).toFixed(3);
        }
      }
      list.push({
        Genista_id: genistaId,
        Result: row[resultCell] ? resultValue : "default",
      });
    } else if (
      nameLower.indexOf("moisture") > -1
      || nameLower.indexOf("fat") > -1
      || nameLower.indexOf("total solids") > -1
      || nameLower.indexOf("fdb") > -1
      || nameLower.indexOf("salt") > -1
      || nameLower.indexOf("wps") > -1
    ) {
      list.push({
        Genista_id: genistaId,
        Result: row[resultCell]
          ? (row[resultCell] * 100).toFixed(3)
          : "default",
      });
    } else if (nameLower.indexOf("water") > -1) {
      list.push({
        Genista_id: genistaId,
        Result: formatLegacyDynamicNumericCell(row[resultCell], 3),
      });
    } else if (nameLower.indexOf("ph") > -1) {
      list.push({
        Genista_id: genistaId,
        Result: formatLegacyDynamicNumericCell(row[resultCell], 2),
      });
    } else if (nameLower.indexOf("brix") > -1) {
      list.push({
        Genista_id: genistaId,
        Result: formatLegacyDynamicNumericCell(row[resultCell], 1),
      });
    } else if (nameLower.indexOf("ffa") > -1) {
      const rowPayload = {
        Genista_id: genistaId,
        Result: "",
      };
      if (typeof row[resultCell] === "number") {
        rowPayload.Result = row[resultCell].toFixed(3);
      } else {
        rowPayload.Result = row[resultCell];
      }
      list.push(rowPayload);
    } else if (nameLower.indexOf("aflatoxin") > -1) {
      const payload = buildAflatoxinRowPayload(row, idCell, resultCell);
      if (payload.error) {
        aflatoxinError = payload.error;
        return { list, aflatoxinError };
      }
      list.push(payload);
    } else {
      const raw = row[resultCell];
      list.push({
        Genista_id: genistaId,
        Result: raw !== undefined && raw !== null && raw !== "" ? raw : "",
      });
    }
  }

  return { list, aflatoxinError: null };
}

/**
 * Builds the chemistry test results list
 * @param {object} params
 * @param {Array} params.rows flat sheet rows OR allergen `[{ sheetData, sheetName }, ...]`
 * @param {string} params.fileName
 * @param {string} params.selectedTestName
 * @returns {Array}
 */
export function buildChemistryTestResultsList({ rows, fileName, selectedTestName }) {
  const list = [];
  const nameLower = normalizeFileName(fileName);

  if (nameLower.indexOf("allergen") > -1) {
    for (const sheet of rows) {
      if (sheet.sheetName === "Treenut") {
        for (const row of sheet.sheetData) {
          if (row[4] && String(row[4]).indexOf("GB") > -1) {
            list.push({
              Genista_id: row[4],
              Result: checkLessThan(row[5]),
              Test: "Tree nut",
            });
          }
        }
      } else if (sheet.sheetName === "Shellf") {
        for (const row of sheet.sheetData) {
          if (row[4] && String(row[4]).indexOf("GB") > -1) {
            list.push({
              Genista_id: row[4],
              Result: checkLessThan(row[6]),
              Test: "Shelfish",
            });
          }
        }
      } else {
        for (const row of sheet.sheetData) {
          if (row[4] && String(row[4]).indexOf("GB") > -1) {
            list.push({
              Genista_id: row[4],
              Result: checkLessThan(row[6]),
              Test: sheet.sheetName.split(" ")[0],
            });
          }
        }
      }
    }
    return list;
  }

  for (const row of rows) {
    if (nameLower.indexOf("anisidine") > -1) {
      if (row[1] && String(row[1]).indexOf("GB") > -1) {
        list.push({
          Genista_id: row[1],
          Result: checkLessThan(row[7]),
        });
      }
    } else if (nameLower.indexOf("sulfite") > -1) {
      if (row[4] && String(row[4]).indexOf("GB") > -1) {
        let resultValue = 0;
        const indexSmall = row[11].toString().indexOf("<");

        if (typeof row[11] === "number") {
          resultValue = row[11];
        } else if (
          (indexSmall === undefined || indexSmall < 0)
          && typeof row[11] === "string"
        ) {
          if (!Number.isNaN(parseFloat(row[11]))) {
            resultValue = parseFloat(row[11]);
          }
        }
        list.push({
          Genista_id: row[4],
          Result: checkLessThan(resultValue),
        });
      }
    } else if (
      (nameLower.indexOf("nitrite") > -1 || nameLower.indexOf("nitrate") > -1)
      && (!nameLower.includes("silver"))
      && selectedTestName === "Nitrite/Nitrate"
    ) {
      if (row[1] && String(row[1]).indexOf("GB") > -1) {
        list.push({
          Genista_id: row[1],
          Result: checkLessThan(row[2]),
          Test: "Nitrite",
        });
        list.push({
          Genista_id: row[1],
          Result: checkLessThan(row[3]),
          Test: "Nitrate",
        });
      }
    } else if (nameLower.indexOf("scoville") > -1) {
      if (row[1] && String(row[1]).indexOf("GB") > -1) {
        list.push({
          Genista_id: row[1],
          Result: row[5],
        });
      }
    } else if (nameLower.indexOf("vitamins a and d") > -1) {
      if (row[1] && String(row[1]).indexOf("GB") > -1) {
        if (row[2] !== "n.a." || row[3] !== "n.a.") {
          let currentResult = "n.a.";
          let currentTest = "n.a.";
          if (row[2] !== "n.a.") {
            currentResult = row[2];
            currentTest = "Vit A (Retinol)";
          } else if (row[3] !== "n.a.") {
            currentResult = row[3];
            currentTest = "Vit A (Palimitate)";
          }
          list.push({
            Genista_id: row[1],
            Result: currentResult,
            Test: currentTest,
          });
        }
        list.push({
          Genista_id: row[1],
          Result: row[4],
          Test: "Vit D",
        });
        list.push({
          Genista_id: row[1],
          Result: row[5],
          Test: "Vit A (Beta Carotene)",
        });
      }
    } else if (nameLower.indexOf("vitamin c") > -1) {
      if (row[1] && String(row[1]).indexOf("GB") > -1) {
        list.push({
          Genista_id: row[1],
          Result: row[2],
          Test: "Vit C",
        });
        list.push({
          Genista_id: row[1],
          Result: row[3],
          Test: "Vit B9",
        });
        list.push({
          Genista_id: row[1],
          Result: row[4],
          Test: "Vit B12",
        });
      }
    } else if (nameLower.indexOf("probiotic") > -1) {
      if (row[0] && String(row[0]).indexOf("GB") > -1) {
        let cellValue = row[7];
        if (cellValue > 10000000) {
          cellValue = Math.round(cellValue / 10000000) * 10000000;
        }
        list.push({
          Genista_id: row[0],
          Result: cellValue,
        });
      }
    }
  }

  return list;
}

/**
 * Builds the heavy metals result list
 * @param {Array<Array>} rows
 * @returns {Array}
 */
export function buildHeavyMetalsUploadList(rows) {
  const list = [];
  const titleRow = rows && rows.length > 0 ? rows[0] : null;

  for (const row of rows) {
    if (row[3] && String(row[3]).indexOf("GB") > -1) {
      row.forEach((columnValue, columnIndex) => {
        let title = "";
        if (columnIndex >= 4) {
          title = titleRow[columnIndex];
        }
        if (title !== "") {
          list.push({
            Genista_id: row[3],
            Test: title,
            Result: String(columnValue).replace("%", "").replaceAll(",", ""),
          });
        }
      });
    }
  }
  return list;
}

/**
 * Builds the punicalagins result list
 * @param {Array<Array>} rows
 * @returns {Array}
 */
export function buildPunicalaginsResultList(rows) {
  const list = [];
  const regEx = new RegExp(GENISTA_ID_ROW_PATTERN);

  for (const row of rows) {
    const result = {
      Genista_id: "",
      Result: {
        A: "",
        B: "",
        "A+B": "",
        A_Dry: "",
        B_Dry: "",
        "A+B_Dry": "",
      },
    };
    if (String(row[1]).match(regEx)) {
      result.Genista_id = row[1];
      result.Result.A = (row[9] * 100).toFixed(3);
      result.Result.B = (row[10] * 100).toFixed(3);
      result.Result["A+B"] = (row[11] * 100).toFixed(3);
      result.Result.A_Dry = (row[13] * 100).toFixed(3);
      result.Result.B_Dry = (row[14] * 100).toFixed(3);
      result.Result["A+B_Dry"] = (row[15] * 100).toFixed(3);
      list.push(result);
    }
  }
  return list;
}

/**
 * Same scaling as legacy `getMositureResult` inside deprecated `parseMoisture`.
 * @param {*} cellValue
 * @returns {string}
 */
export function formatMoistureCellResult(cellValue) {
  if (cellValue && cellValue !== "") {
    const scaled = (cellValue * 100).toFixed(3);
    if (scaled !== "NaN") {
      return scaled;
    }
  }
  return "default";
}

const MOISTURE_TEMPLATE_COLUMN_MAP = [
  { test: "Moisture", cell: 5 },
  { test: "Total Solids", cell: 6 },
  { test: "Fat", cell: 7 },
  { test: "FDB", cell: 9 },
  { test: "Salt", cell: 12 },
  { test: "WPS", cell: 13 },
];

/**
 * Builds the moisture result list
 * @param {Array<Array>} rows
 * @returns {Array}
 */
export function buildMoistureMultiTestList(rows) {
  const list = [];
  rows.forEach((row) => {
    if (row[1] && String(row[1]).indexOf("GB") > -1) {
      MOISTURE_TEMPLATE_COLUMN_MAP.forEach((mapping) => {
        list.push({
          Genista_id: row[1].trim(),
          Test: mapping.test,
          Result: formatMoistureCellResult(row[mapping.cell]),
        });
      });
    }
  });
  return list;
}

/**
 * Builds the polyphenols result list
 * @param {Array<Array>} rows
 * @returns {Array}
 */
export function buildPolyphenolsResultList(rows) {
  const list = [];
  const regEx = new RegExp(GENISTA_ID_ROW_PATTERN);

  for (const row of rows) {
    const result = {
      Genista_id: "",
      Result: { Poly: "", "Poly Dry Basis": "" },
    };
    if (String(row[0]).match(regEx)) {
      result.Genista_id = row[0];
      result.Result.Poly = (row[5] * 100).toFixed(3);
      result.Result["Poly Dry Basis"] = (row[7] * 100).toFixed(3);
      list.push(result);
    }
  }
  return list;
}

/**
 * @param {Array<Array>} rows
 * @returns {Array}
 */
export function buildSilverNitrateResultList(rows) {
  const list = [];
  const regEx = new RegExp(GENISTA_ID_ROW_PATTERN);

  for (const row of rows) {
    const result = { Genista_id: "", Result: "" };
    if (String(row[0]).match(regEx)) {
      result.Genista_id = row[0];
      result.Result = row[5] * 100;
      list.push(result);
    }
  }
  return list;
}

/**
 * @param {Array<Array>} rows
 * @returns {Array}
 */
export function buildMycotoxinUploadList(rows) {
  const list = [];
  let isAflatoxinSection = false;
  let isFumonisinSection = false;

  for (const row of rows) {
    let aflatoxinSum = 0;
    let aflatoxinGenistaId = "";
    let fumonisinSum = 0;
    let fumonisinGenistaId = "";
    let fumonisin = { Genista_id: "", Result: "", Test: "Fumonisin" };
    let aflatoxin = {
      Genista_id: "",
      Result: "",
      Test: "Aflatoxin (B1, B2, G1, G2)",
    };
    const element = row;
    const regEx = new RegExp(GENISTA_ID_ROW_PATTERN);
    const AflatoxinB1 = { Genista_id: "", Result: "", Test: "" };
    const AflatoxinB2 = { Genista_id: "", Result: "", Test: "" };
    const AflatoxinG1 = { Genista_id: "", Result: "", Test: "" };
    const AflatoxinG2 = { Genista_id: "", Result: "", Test: "" };
    const Ochratoxin = { Genista_id: "", Result: "", Test: "" };
    const zearalenone = { Genista_id: "", Result: "", Test: "" };
    const deoxynivalenol = { Genista_id: "", Result: "", Test: "" };
    const fumonisinB1 = { Genista_id: "", Result: "", Test: "" };
    const fumonisinB2 = { Genista_id: "", Result: "", Test: "" };
    const fumonisinB3 = { Genista_id: "", Result: "", Test: "" };
    const t2Toxin = { Genista_id: "", Result: "", Test: "" };
    const ht2Toxin = { Genista_id: "", Result: "", Test: "" };

    if (element[2].toString().includes("Aflatoxin")) {
      isAflatoxinSection = true;
      isFumonisinSection = false;
    }
    if (
      element[2].toString().includes("Fumonison")
      || element[2].toString().includes("Fumonisin")
    ) {
      isFumonisinSection = true;
      isAflatoxinSection = false;
    }
    if (String(element[1]).match(regEx)) {
      if (isAflatoxinSection) {
        AflatoxinB1.Genista_id = element[1];
        AflatoxinB1.Result = checkLessThan(element[2]);
        AflatoxinB2.Result = checkLessThan(element[3]);
        AflatoxinG1.Result = checkLessThan(element[4]);
        AflatoxinG2.Result = checkLessThan(element[5]);
        aflatoxinGenistaId = AflatoxinB1.Genista_id;
        aflatoxinSum = aflatoxinSum
          + AflatoxinB1.Result
          + AflatoxinB2.Result
          + AflatoxinG1.Result
          + AflatoxinG2.Result;
        aflatoxin = {
          Genista_id: aflatoxinGenistaId,
          Result: aflatoxinSum,
          Test: "Aflatoxin (B1, B2, G1, G2)",
        };
        list.push(aflatoxin);
        Ochratoxin.Genista_id = element[1];
        Ochratoxin.Result = checkLessThan(element[6]);
        Ochratoxin.Test = "OCH";
        list.push(Ochratoxin);
        zearalenone.Genista_id = element[1];
        zearalenone.Result = checkLessThan(element[7]);
        zearalenone.Test = "ZEA";
        list.push(zearalenone);
        deoxynivalenol.Genista_id = element[1];
        deoxynivalenol.Result = checkLessThan(element[8]);
        deoxynivalenol.Test = "DON";
        list.push(deoxynivalenol);
      }
      if (isFumonisinSection) {
        fumonisinB1.Genista_id = element[1];
        fumonisinB1.Result = checkLessThan(element[2]);
        fumonisinB2.Result = checkLessThan(element[3]);
        fumonisinB3.Result = checkLessThan(element[4]);
        fumonisinGenistaId = fumonisinB1.Genista_id;
        fumonisinSum = fumonisinSum
          + fumonisinB1.Result
          + fumonisinB2.Result
          + fumonisinB3.Result;
        fumonisin = {
          Genista_id: fumonisinGenistaId,
          Result: fumonisinSum,
          Test: "Fumonisin",
        };
        list.push(fumonisin);
        t2Toxin.Genista_id = element[1];
        t2Toxin.Result = checkLessThan(element[5]);
        t2Toxin.Test = "T-2";
        list.push(t2Toxin);
        ht2Toxin.Genista_id = element[1];
        ht2Toxin.Result = checkLessThan(element[6]);
        ht2Toxin.Test = "HT-2";
        list.push(ht2Toxin);
      }
    }
  }
  return list;
}

/**
 * @param {Array<Array>} rows
 * @returns {Array}
 */
export function buildTitratableAcidityUploadList(rows) {
  const list = [];
  const regEx = new RegExp(GENISTA_ID_ROW_PATTERN);

  for (const row of rows) {
    const resultCitric = { Genista_id: "", Result: "", Test: "" };
    const resultAcetic = { Genista_id: "", Result: "", Test: "" };
    if (String(row[0]).match(regEx)) {
      resultCitric.Genista_id = row[0];
      if (row[6] === "REDO") {
        resultCitric.Result = "REDO";
      } else {
        resultCitric.Result = row[6];
      }
      resultCitric.Test = "Titratable Acidity";
      list.push(resultCitric);
      resultAcetic.Genista_id = row[0];
      if (row[7] === "REDO") {
        resultAcetic.Result = "REDO";
      } else {
        resultAcetic.Result = row[7];
      }
      resultAcetic.Test = "Titratable Acidity-Acetic Acid";
      list.push(resultAcetic);
    }
  }
  return list;
}

/**
 * @param {object} params
 * @param {Array<Array>} params.rows
 * @param {Array} params.testList
 * @param {string} params.selectedTestName
 * @returns {Array<{ genista_id: string, result: Array }>}
 */
export function buildDynamicOneToOneList({ rows, testList, selectedTestName }) {
  let genIdIndex = 0;
  let resultIndex = 0;
  let resultType = "";
  let units = "";

  for (let index = 0; index < testList.length; index++) {
    const currentTest = testList[index];
    if (currentTest.test === selectedTestName) {
      genIdIndex = currentTest.genistaid_cell;
      resultIndex = currentTest.result_cell;
      resultType = currentTest.result_type;
      units = currentTest.units.toLowerCase().trim();
    }
  }

  const columnOffset = resultIndex - genIdIndex;
  const list = [];
  const regEx = new RegExp(GENISTA_ID_ROW_PATTERN);

  for (let rowIndex = 0; rowIndex < rows.length; rowIndex++) {
    if (String(rows[rowIndex]).match(regEx)) {
      let genistaIdColumn = 0;
      for (let columnIndex = 0; columnIndex < rows[rowIndex].length; columnIndex++) {
        if (String(rows[rowIndex][columnIndex]).match(regEx)) {
          genistaIdColumn = columnIndex;
        }
      }
      const genistaIdValue = rows[rowIndex][genistaIdColumn];
      let resultValue = rows[rowIndex][genistaIdColumn + columnOffset];
      if (String(resultValue).includes("<")) {
        resultValue = 0;
      }

      if (PERCENT_UNIT_OPTIONS.indexOf(units) !== -1 && resultType === "float") {
        resultValue = (resultValue * 100).toFixed(3);
      } else if (resultType === "float") {
        resultValue = resultValue.toFixed(3);
      }
      list.push({
        genista_id: genistaIdValue,
        result: [resultValue],
      });
    }
  }
  return list;
}

/**
 * @param {object} params
 * @param {Array<Array>} params.rows
 * @param {Array} params.testList
 * @param {string} params.selectedTestName
 * @returns {Array<{ genista_id: string, result: Array }>}
 */
export function buildDynamicOneToManyList({ rows, testList, selectedTestName }) {
  let resultOffsets = [];
  let resultType = "";
  let units = "";

  for (let index = 0; index < testList.length; index++) {
    const currentTest = testList[index];
    if (currentTest.test === selectedTestName) {
      const genIdIndex = parseInt(currentTest.genistaid_cell, 10);
      resultOffsets = currentTest.result_cell.split(",").map((item) => parseInt(item, 10) - genIdIndex);
      resultType = currentTest.result_type;
      units = currentTest.units.toLowerCase().trim();
    }
  }

  const list = [];
  const regEx = new RegExp(GENISTA_ID_ROW_PATTERN);

  for (let rowIndex = 0; rowIndex < rows.length; rowIndex++) {
    if (String(rows[rowIndex]).match(regEx)) {
      let genistaIdColumn = 0;
      for (let columnIndex = 0; columnIndex < rows[rowIndex].length; columnIndex++) {
        if (String(rows[rowIndex][columnIndex]).match(regEx)) {
          genistaIdColumn = columnIndex;
        }
      }
      const resultList = [];
      for (let offsetIndex = 0; offsetIndex < resultOffsets.length; offsetIndex++) {
        const relativeIndex = resultOffsets[offsetIndex];
        let cellValue = rows[rowIndex][genistaIdColumn + relativeIndex];
        if (String(cellValue).includes("<")) {
          cellValue = 0;
        }
        if (PERCENT_UNIT_OPTIONS.indexOf(units) !== -1 && resultType === "float") {
          cellValue = (cellValue * 100).toFixed(3);
        } else if (resultType === "float") {
          cellValue = cellValue.toFixed(3);
        }
        resultList.push(cellValue);
      }
      list.push({
        genista_id: rows[rowIndex][genistaIdColumn],
        result: resultList,
      });
    }
  }
  return list;
}

/**
 * First GB-style ID from FAP sheet (legacy `getGenistaID`).
 * @param {Array<Array>} rows
 * @returns {string|null}
 */
export function extractFapGenistaIdFromRows(rows) {
  const gbRow = rows.find((row) => String(row).includes("GB"));
  const rawValue = String(gbRow?.[2] || "");
  const idParts = rawValue.split("_");
  if (idParts.length < 2) {
    return null;
  }
  return `${idParts[0].replace(/\s+/g, "")}_${idParts[1].replace(/\s+/g, "")}`;
}

/** Genista ID shape used by Residual Solvents / FAP uploads: GB{date}_{counter}. */
const RESIDUAL_SOLVENTS_GENISTA_ID_PATTERN = /GB\d+_\d+/i;

/**
 * First GB-style ID anywhere in the sheet (any cell). Residual Solvents places the ID
 * in A1 today, but it may appear elsewhere in future templates.
 *
 * @param {Array<Array>} rows
 * @returns {string|null}
 */
export function extractResidualSolventsGenistaIdFromRows(rows) {
  if (!Array.isArray(rows)) {
    return null;
  }
  for (const row of rows) {
    if (!Array.isArray(row)) {
      continue;
    }
    for (const cell of row) {
      if (cell === undefined || cell === null || cell === "" || cell === "default") {
        continue;
      }
      const match = String(cell).replace(/\s+/g, "").match(RESIDUAL_SOLVENTS_GENISTA_ID_PATTERN);
      if (match) {
        return match[0];
      }
    }
  }
  return null;
}

/**
 * Normalizes Residual Solvents Excel cells for PDF rendering.
 * `readChemUploadPrimaryWorksheetRows` uses defval `"default"` for blanks.
 *
 * @param {unknown} cell
 * @returns {string}
 */
function residualSolventsCellToText(cell) {
  if (cell === undefined || cell === null || cell === "" || cell === "default") {
    return "";
  }
  return String(cell).trim();
}

/**
 * @param {Array|undefined|null} row
 * @param {number} columnCount
 * @returns {boolean}
 */
function isResidualSolventsRowEmpty(row, columnCount) {
  for (let columnIndex = 0; columnIndex < columnCount; columnIndex += 1) {
    if (residualSolventsCellToText(row?.[columnIndex])) {
      return false;
    }
  }
  return true;
}

/** Required Residual Solvents -USP Excel header (exactly 5 columns, in order). */
export const RESIDUAL_SOLVENTS_EXPECTED_HEADERS = [
  "Class",
  "Analyte / Parameter",
  "LOQ",
  "Result",
  "Pass/Fail",
];

const RESIDUAL_SOLVENTS_EXPECTED_HEADERS_NORMALIZED = RESIDUAL_SOLVENTS_EXPECTED_HEADERS.map(
  (header) => header.toLowerCase().replace(/\s+/g, " "),
);

/**
 * @param {unknown} value
 * @returns {string}
 */
function normalizeResidualSolventsHeaderName(value) {
  return residualSolventsCellToText(value).toLowerCase().replace(/\s+/g, " ");
}

/**
 * Finds the Residual Solvents header row (Class / Analyte / Parameter).
 * No generic multi-column fallback — wrong headers produce a bad PDF.
 *
 * @param {Array<Array>} rows
 * @returns {number}
 */
function findResidualSolventsHeaderRowIndex(rows) {
  for (let rowIndex = 0; rowIndex < rows.length; rowIndex += 1) {
    const row = rows[rowIndex];
    if (!Array.isArray(row)) {
      continue;
    }
    const firstCell = normalizeResidualSolventsHeaderName(row[0]);
    const secondCell = normalizeResidualSolventsHeaderName(row[1]);
    if (
      firstCell === "class"
      || secondCell.includes("analyte")
      || secondCell.includes("parameter")
    ) {
      return rowIndex;
    }
  }
  return -1;
}

/**
 * @param {string[]} headers
 * @returns {string|null}
 */
function getResidualSolventsHeaderValidationError(headers) {
  if (headers.length !== RESIDUAL_SOLVENTS_EXPECTED_HEADERS.length) {
    return (
      `Invalid Residual Solvents header: expected ${RESIDUAL_SOLVENTS_EXPECTED_HEADERS.length} `
      + `columns, found ${headers.length}.`
    );
  }

  for (let columnIndex = 0; columnIndex < headers.length; columnIndex += 1) {
    const actualNormalized = normalizeResidualSolventsHeaderName(headers[columnIndex]);
    const expectedNormalized = RESIDUAL_SOLVENTS_EXPECTED_HEADERS_NORMALIZED[columnIndex];
    if (actualNormalized !== expectedNormalized) {
      return (
        `Invalid Residual Solvents header: column ${columnIndex + 1} expected `
        + `"${RESIDUAL_SOLVENTS_EXPECTED_HEADERS[columnIndex]}", `
        + `found "${headers[columnIndex] || "(empty)"}".`
      );
    }
  }

  return null;
}

/**
 * Parses Residual Solvents -USP result table with a fixed 5-column header.
 * Renders Excel text as-is — no rounding or analyte mapping.
 *
 * @param {Array<Array>} rows
 * @returns {{ headers: string[], body: string[][], error: string|null }}
 */
export function buildResidualSolventsTableFromRows(rows) {
  if (!Array.isArray(rows) || rows.length === 0) {
    return {
      headers: [],
      body: [],
      error: "Could not parse Residual Solvents results from the Excel file",
    };
  }

  const headerRowIndex = findResidualSolventsHeaderRowIndex(rows);
  if (headerRowIndex < 0) {
    return {
      headers: [],
      body: [],
      error: "Could not find Residual Solvents header row (Class / Analyte or Parameter).",
    };
  }

  const headerRow = rows[headerRowIndex];
  let columnCount = 0;
  for (let columnIndex = 0; columnIndex < headerRow.length; columnIndex += 1) {
    if (residualSolventsCellToText(headerRow[columnIndex])) {
      columnCount = columnIndex + 1;
    }
  }

  const headers = [];
  for (let columnIndex = 0; columnIndex < columnCount; columnIndex += 1) {
    headers.push(residualSolventsCellToText(headerRow[columnIndex]));
  }

  const headerError = getResidualSolventsHeaderValidationError(headers);
  if (headerError) {
    return { headers: [], body: [], error: headerError };
  }

  // Fixed 5-column layout after validation.
  columnCount = RESIDUAL_SOLVENTS_EXPECTED_HEADERS.length;

  const body = [];
  for (let rowIndex = headerRowIndex + 1; rowIndex < rows.length; rowIndex += 1) {
    const row = rows[rowIndex];
    if (isResidualSolventsRowEmpty(row, columnCount)) {
      if (body.length > 0) {
        break;
      }
      continue;
    }
    const bodyRow = [];
    for (let columnIndex = 0; columnIndex < columnCount; columnIndex += 1) {
      bodyRow.push(residualSolventsCellToText(row?.[columnIndex]));
    }
    body.push(bodyRow);
  }

  if (body.length === 0) {
    return {
      headers: [],
      body: [],
      error: "Could not parse Residual Solvents results from the Excel file",
    };
  }

  // Use canonical header labels in the PDF.
  return {
    headers: [...RESIDUAL_SOLVENTS_EXPECTED_HEADERS],
    body,
    error: null,
  };
}

/**
 * @param {string[]} headers
 * @returns {Record<number, object>}
 */
function buildResidualSolventsPdfColumnStyles(headers) {
  const columnStyles = {};
  const columnCount = headers.length || 1;
  // Keep fixed-ish widths for short columns; give leftover width to analyte names so they wrap.
  const shortColumnWidth = Math.min(72, Math.floor(520 / Math.max(columnCount, 1)));
  headers.forEach((header, columnIndex) => {
    const lower = String(header).toLowerCase();
    if (lower.includes("analyte") || lower.includes("parameter")) {
      columnStyles[columnIndex] = { cellWidth: "auto", minCellWidth: 140, overflow: "linebreak" };
    } else if (lower.includes("pass") || lower === "class") {
      columnStyles[columnIndex] = { halign: "center", cellWidth: shortColumnWidth };
    } else if (lower.includes("loq") || lower.includes("result")) {
      columnStyles[columnIndex] = { halign: "center", cellWidth: Math.max(shortColumnWidth, 80), overflow: "linebreak" };
    } else {
      columnStyles[columnIndex] = { overflow: "linebreak" };
    }
  });
  return columnStyles;
}

/**
 * Builds a Residual Solvents PDF from a dynamic Excel table and API sample metadata.
 * Supports multi-page output; long analyte names wrap instead of truncating.
 * Table headers/cells are rendered as in the Excel. Genista ID is omitted from PDF content.
 *
 * @param {object} params
 * @param {string[]} params.headers
 * @param {string[][]} params.body
 * @param {string} params.fileName
 * @param {{ sample_id?: string, sample_type?: string }} params.apiResponse
 * @returns {File}
 */
export function generateResidualSolventsPdfFile({
  headers,
  body,
  fileName,
  apiResponse,
}) {
  if (!Array.isArray(headers) || headers.length === 0 || !Array.isArray(body) || body.length === 0) {
    throw new Error("Residual Solvents PDF requires a parsed header and at least one data row");
  }

  const document = new JSPDF({ format: "letter", unit: "pt" });
  const pageMargin = 40;
  const pageWidth = document.internal.pageSize.getWidth();
  const contentWidth = pageWidth - pageMargin * 2;

  document.setFontSize(12);
  document.setFont("Helvetica", "bold");
  document.text("Residual Solvents Results", pageWidth / 2, pageMargin, { align: "center" });

  let cursorY = pageMargin + 20;
  document.setFontSize(9);

  const drawLabeledValue = (label, value) => {
    const safeValue = value == null || value === "" ? "—" : String(value);
    document.setFont("Helvetica", "bold");
    document.text(label, pageMargin, cursorY);
    const labelWidth = document.getTextWidth(label) + 4;
    document.setFont("Helvetica", "normal");
    const lines = document.splitTextToSize(safeValue, Math.max(contentWidth - labelWidth, 40));
    document.text(lines, pageMargin + labelWidth, cursorY);
    cursorY += Math.max(document.getTextDimensions(lines).h, 11) + 4;
  };

  drawLabeledValue("Sample ID:", apiResponse?.sample_id);
  drawLabeledValue("Sample Type:", apiResponse?.sample_type);
  cursorY += 8;

  document.autoTable({
    startY: cursorY,
    head: [headers],
    body,
    theme: "plain",
    showHead: "everyPage",
    styles: {
      cellPadding: 3,
      lineColor: 50,
      fontSize: 8,
      lineWidth: 0.5,
      textColor: 0,
      overflow: "linebreak",
      valign: "top",
    },
    headStyles: {
      fontStyle: "bold",
      halign: "center",
      valign: "middle",
      fontSize: 8,
      overflow: "linebreak",
    },
    columnStyles: buildResidualSolventsPdfColumnStyles(headers),
    margin: {
      left: pageMargin, right: pageMargin, top: pageMargin, bottom: pageMargin,
    },
  });

  const blob = document.output("blob");
  return new File([blob], fileName, { type: "application/pdf" });
}

/**
 * @param {Array<Array>} rows
 * @param {string} xlsName
 * @returns {string|null|number}
 */
export function getFapCellResultForXlsName(rows, xlsName) {
  let pattern = new RegExp(xlsName.toLowerCase());
  if (xlsName === "Saturated Fat") {
    pattern = /^saturated fat$/;
  }
  let matchedValue = null;
  rows.forEach((row) => {
    const normalizedLabel = String(row[0])
      .replace(/\s+/g, " ")
      .trim(" ")
      .toLowerCase();
    if (normalizedLabel.match(pattern)) {
      matchedValue = row[13];
      if (typeof matchedValue === "number") {
        matchedValue = matchedValue.toFixed(2);
      }
      if (matchedValue === "100.00" || matchedValue === 100.00) {
        if (xlsName === "Saturated Fat") {
          matchedValue = "99.99";
        }
        if (xlsName === "Total Fat") {
          matchedValue = "-";
        }
      }
    }
  });
  return matchedValue;
}

/**
 * Builds rows for FAP PDF table body (legacy `getAnalysisResults`).
 * @param {Array<Array>} rows
 * @param {Array<{ reportName: string, xlsName: string }>} nameMapping
 * @returns {Array<{ analysis: string, name: string, limit: number, result: *, units: string }>}
 */
export function buildFapAnalyticalResults(rows, nameMapping) {
  const analyticalResults = [];
  nameMapping.forEach((item, index) => {
    let analysisRow = {};
    if (index === 0) {
      analysisRow = {
        analysis: "Fatty Acid Profile:",
        name: item.reportName,
        limit: 0.01,
        result: getFapCellResultForXlsName(rows, item.xlsName),
        units: "%",
      };
    } else {
      analysisRow = {
        analysis: " ",
        name: item.reportName,
        limit: 0.01,
        result: getFapCellResultForXlsName(rows, item.xlsName),
        units: "%",
      };
    }
    analyticalResults.push(analysisRow);
  });
  return analyticalResults;
}

export const FAP_ANALYTE_MAPPING = [
  { reportName: "4:0 Butyric", xlsName: "C04:0 Methyl butyrate" },
  { reportName: "6:0 Caproic", xlsName: "C06:0 Caproic" },
  { reportName: "8:0 Caprylic", xlsName: "C08:0 Caprylic" },
  { reportName: "10:0 Capric", xlsName: "C10:0 Capric" },
  { reportName: "11:0 Undecanoic", xlsName: "C11:0 Methyl undecanoate" },
  { reportName: "12:0 Lauric", xlsName: "C12:0 Lauric" },
  { reportName: "13:0 Tridecanoic", xlsName: "C13:0 Methyl tridecanoate" },
  { reportName: "14:0 Myristic", xlsName: "C14:0 Myristic" },
  { reportName: "14:1 Myristoleic", xlsName: "C14:1 Myristoleic" },
  { reportName: "15:0 Pentadecanoic", xlsName: "C15:0 Pentadecanoic" },
  { reportName: "15:1 Pentadecenoic", xlsName: "C15:1 Pentadecenoic" },
  { reportName: "16:0 Palmitic", xlsName: "C16:0 Palmitic" },
  { reportName: "16:1 Palmitoleic", xlsName: "C16:1 Palmitoleic" },
  { reportName: "17:0 Heptadecanoic", xlsName: "C17:0 Heptadecanoic" },
  { reportName: "17:1 Heptadecenoic", xlsName: "C17:1 Heptadecenoic" },
  { reportName: "18:0 Stearic", xlsName: "C18:0 Stearic" },
  { reportName: "18:1 trans-Elaidic", xlsName: "C18:1 Elaidic acid-trans" },
  { reportName: "18:1 cis-Oleic", xlsName: "C18:1 Oleic-cis" },
  { reportName: "18:1:n-7 cis-Vaccenic", xlsName: "C18:1 n-7 Vaccenic acid" },
  { reportName: "18:2 trans-Linoleic", xlsName: "C18:2 Linoleic-trans" },
  { reportName: "18:2 cis-Linoleic", xlsName: "C18:2 Linoleic-cis" },
  { reportName: "18:3 Gamma Linolenic", xlsName: "C18:3 Gamma Linoleic" },
  { reportName: "18:3 Alpha Linolenic", xlsName: "C18:3 Alpha Linoleic" },
  { reportName: "20:0 Arachidic", xlsName: "C20:0 Arachidic" },
  { reportName: "20:1 Eicosenoic", xlsName: "C20:1 Eicosenoic" },
  { reportName: "20:2 Eicosadienoic", xlsName: "C20:2 Eicosadienoic" },
  { reportName: "20:3 Eicosatrienoic", xlsName: "C20:3 Eicosatrienoic" },
  { reportName: "20:3 Homogamma Linolenic", xlsName: "C20:3 Homogamma Linolenic" },
  { reportName: "20:4 Arachidonic (n6)", xlsName: "C20:4 Arachidonic" },
  { reportName: "20:5 Eicosapentaenoic", xlsName: "C20:5 Eicosapentaenoic" },
  { reportName: "21:0 Heneicosanoic", xlsName: "C21:0 Methyl heneicosanoate" },
  { reportName: "22:0 Behenic", xlsName: "C22:0 Behenic" },
  { reportName: "22:1 Erucic", xlsName: "C22:1 Erucic" },
  { reportName: "22:2 Docosadienoic", xlsName: "C22:2 Docosadienoic" },
  { reportName: "22:6 Docosahexaenoic", xlsName: "C22:6 Docosahexaenoic" },
  { reportName: "23:0 Tricosanoic", xlsName: "C23:0 Methyl tricosanoate" },
  { reportName: "24:0 Lignoceric", xlsName: "C24:0 Lignoceric" },
  { reportName: "24:1 Nervonic", xlsName: "C24:1 Nervonic" },
  { reportName: "Saturated Fat", xlsName: "Saturated Fat" },
  { reportName: "Monounsaturated Fat", xlsName: "Mono-unsaturated Fat" },
  { reportName: "Polyunsaturated Fat", xlsName: "Poly-unsaturated" },
  { reportName: "Omega 3 Fatty Acids", xlsName: "Omega-3" },
  { reportName: "Omega 6 Fatty Acids", xlsName: "Omega-6" },
  { reportName: "Total Fatty Acids", xlsName: "Total Fat" },
  { reportName: "Trans Fat", xlsName: "Trans Fat" },
];

/**
 * Builds a FAP PDF file from analytical rows and API metadata.
 * @param {object} params
 * @param {Array} params.analyticalResults
 * @param {string} params.fileName
 * @param {object} params.fapApiResponse
 * @returns {File}
 */
export function generateFapPdfFile({ analyticalResults, fileName, fapApiResponse }) {
  const document = new JSPDF({ format: "letter", unit: "pt" });
  const pageMargin = 40;
  document.setFontSize(10);
  document.setFont(undefined, "bold");
  document.text("Fatty Acid Profile Results", 230, pageMargin);

  const tableHead = [
    [
      { content: "Analysis", colSpan: 2, styles: { halign: "center", fontSize: 10 } },
      { content: "Detection Limit", colSpan: 1, styles: { halign: "center", fontSize: 10 } },
      { content: "Result", colSpan: 1, styles: { halign: "center", fontSize: 10 } },
      { content: "Units", colSpan: 1, styles: { halign: "center", fontSize: 10 } },
    ],
  ];

  document.setFontSize(9);
  const sampleIdLines = document.splitTextToSize(fapApiResponse.sample_id, 490);
  const sampleIdHeight = document.getTextDimensions(sampleIdLines).h;
  document.setFont("Helvetica", "Bold");
  document.text("Sample ID:", 40, 60);
  document.setFont("Helvetica", "normal");
  document.text(sampleIdLines, 90, 60);

  const sampleTypeLines = document.splitTextToSize(fapApiResponse.sample_type, 490);
  const sampleTypeHeight = document.getTextDimensions(sampleTypeLines).h;
  document.setFont("Helvetica", "Bold");
  document.text("Sample Type:", 40, 70 + sampleIdHeight);
  document.setFont("Helvetica", "normal");
  document.text(sampleTypeLines, 101, 70 + sampleIdHeight);

  document.autoTable({
    startY: sampleIdHeight + sampleTypeHeight + 70,
    head: tableHead,
    body: analyticalResults,
    columns: [
      { header: "Analysis", dataKey: "analysis" },
      { header: "Name", dataKey: "name" },
      { header: "Limit", dataKey: "limit" },
      { header: "Result", dataKey: "result" },
      { header: "Units", dataKey: "units" },
    ],
    theme: "plain",
    styles: {
      cellPadding: 2,
      lineColor: 50,
      fontSize: 9,
      lineWidth: 1,
      textColor: 0,
    },
    columnStyles: {
      result: { halign: "center" },
      limit: { halign: "center" },
    },
  });

  const blob = document.output("blob");
  return new File([blob], fileName, { type: "application/pdf" });
}

/**
 * Create an array of each test and it's result for a GBID
 * @param {string} sampleName
 * @param {string[]} row
 * @param {Map<number, string>} testIndexMap
 */
const getSampleValueList = (sampleName, row, testIndexMap) => {
  const list = [];
  row.forEach((colElement, index) => {
    const testName = testIndexMap.get(index);
    let result = "";
    // A measured concentration of zero is valid and must not be treated as an empty cell.
    if (testName && (colElement || colElement === 0)) {
      result = colElement.toString().replace("%", "").replace(/,/g, "");
    }

    if (result) {
      list.push({
        Genista_id: sampleName,
        Test: testName,
        Result: result,
      });
    }
  });
  return list;
};

/**
 * New method to parse heavy metals excel sheet
 * @param {string[][]} rows rows from excel
 * @param {File} file file object
 */
export const buildAgilentMetalsUploadList = (rows) => {
  const list = [];
  const titleRow = rows && rows.length > 0 ? rows[0] : null;

  if (!titleRow) {
    return [];
  }

  // create test map from title row
  const testIndexMap = new Map();
  titleRow.forEach((item, index) => {
    if (item !== "default" && item !== "Sample") {
      testIndexMap.set(index, item);
    }
  });

  rows.slice(1).forEach((row) => {
    const sampleColumn = row[6];
    if (sampleColumn && sampleColumn.toString().indexOf("GB") > -1) {
      const testValueList = getSampleValueList(sampleColumn, row, testIndexMap);
      list.push(...testValueList);
    }
  });

  return list;
};

/**
 * Reads the worksheet legacy upload used for non-allergen files (`readXcelFile` + sheet index).
 *
 * @param {File} file
 * @returns {Promise<Array<Array>>}
 */
export const readChemUploadPrimaryWorksheetRows = async (file) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = (event) => {
    try {
      const workbook = XLSX.read(event.target.result, { type: "array", bookVBA: true });
      const sheetNames = workbook.SheetNames || [];
      let sheetIndex = getLegacyChemUploadPrimarySheetIndexFromFileName(file.name);
      if (sheetIndex < 0 || sheetIndex >= sheetNames.length) {
        sheetIndex = 0;
      }
      const worksheet = workbook.Sheets[sheetNames[sheetIndex]];
      const sheetRows = XLSX.utils.sheet_to_json(worksheet, { header: 1, defval: "default" });
      resolve(sheetRows);
    } catch (error) {
      reject(error);
    }
  };
  reader.onerror = () => reject(new Error("Failed to read uploaded file"));
  reader.readAsArrayBuffer(file);
});

/**
 * Allergen workbooks use every sheet; other SSANN(silver nitrate) templates use the legacy primary sheet index
 * ("readXcelFile" / "readXcelFileAllergen").
 *
 * @param {File} file
 * @returns {Promise<Array|Array<{ sheetData: Array, sheetName: string }>>}
 */
export const readChemUploadSsannWorksheetPayload = async (file) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = (event) => {
    try {
      const workbook = XLSX.read(event.target.result, { type: "array", bookVBA: true });
      const fileNameLower = normalizeFileName(file.name);
      if (fileNameLower.includes("allergen")) {
        const sheetPayloadList = [];
        for (let sheetIndex = 0; sheetIndex < workbook.SheetNames.length; sheetIndex += 1) {
          const sheetName = workbook.SheetNames[sheetIndex];
          const worksheet = workbook.Sheets[sheetName];
          const sheetData = XLSX.utils.sheet_to_json(worksheet, { header: 1, defval: "default" });
          sheetPayloadList.push({ sheetData, sheetName });
        }
        resolve(sheetPayloadList);
        return;
      }
      const sheetNames = workbook.SheetNames || [];
      let sheetIndex = getLegacyChemUploadPrimarySheetIndexFromFileName(file.name);
      if (sheetIndex < 0 || sheetIndex >= sheetNames.length) {
        sheetIndex = 0;
      }
      const primaryWorksheet = workbook.Sheets[sheetNames[sheetIndex]];
      const primarySheetRows = XLSX.utils.sheet_to_json(primaryWorksheet, { header: 1, defval: "default" });
      resolve(primarySheetRows);
    } catch (error) {
      reject(error);
    }
  };
  reader.onerror = () => reject(new Error("Failed to read uploaded file"));
  reader.readAsArrayBuffer(file);
});
